import { UnexpectedError } from '@sparelabs/error-types'
import { IWindow } from '../scheduling/TimeConstraintTypes'
import { RequestIntentType } from '../types/RequestTypes'
import { ArriveByPickupConstraintType } from '../types/ServiceTypes'
import { IRequestConstraints } from './RequestConstraintsTypes'

/**
 * Contains logic that acts on top of request constraints.
 */
export class RequestConstraintUtils {
  public static getEarliestSchedulablePickupTs(constraints: IRequestConstraints): number {
    if (constraints.pickupWindow) {
      return constraints.pickupWindow.min
    }
    return constraints.dropoffWindow.min - constraints.maxTimeInVehicle
  }

  public static getLatestSchedulablePickupTs(constraints: IRequestConstraints, minTravelDuration: number): number {
    if (constraints.pickupWindow) {
      return constraints.pickupWindow.max
    }
    return constraints.dropoffWindow.max - minTravelDuration
  }

  public static getEarliestSchedulableDropoffTs(
    constraints: IRequestConstraints,
    minTravelDuration: number,
    pickupCompletedTs?: number,
  ): number {
    if (constraints.dropoffWindow) {
      return constraints.dropoffWindow.min
    }
    return (pickupCompletedTs ?? constraints.pickupWindow.min) + minTravelDuration
  }

  public static getLatestSchedulableDropoffTs(constraints: IRequestConstraints, pickupEta: number | null): number {
    if (constraints.dropoffWindow) {
      return constraints.dropoffWindow.max
    }
    return Math.max(pickupEta ?? 0, constraints.pickupWindow.max) + constraints.maxTimeInVehicle
  }

  public static getDesiredPickupTs(constraints: IRequestConstraints, travelDurationSeconds: number): number {
    if (constraints.pickupWindow) {
      return constraints.pickupWindow.desired
    }
    return constraints.dropoffWindow.desired - travelDurationSeconds
  }

  public static toValidConstraints(constraints: {
    pickupWindow: IWindow | null
    dropoffWindow: IWindow | null
    maxTimeInVehicle: number
  }): IRequestConstraints {
    // Typescript cannot figure out the types if the two conditions are combined.
    if (constraints.pickupWindow) {
      return {
        pickupWindow: constraints.pickupWindow,
        dropoffWindow: constraints.dropoffWindow,
        maxTimeInVehicle: constraints.maxTimeInVehicle,
      }
    }

    if (constraints.dropoffWindow) {
      return {
        pickupWindow: constraints.pickupWindow,
        dropoffWindow: constraints.dropoffWindow,
        maxTimeInVehicle: constraints.maxTimeInVehicle,
      }
    }

    throw new UnexpectedError('Invalid constraints: at least one window must be defined')
  }

  public static validateWindowsForType(
    pickupWindow: IWindow | null,
    dropoffWindow: IWindow | null,
    intentType: RequestIntentType,
    arriveByPickupConstraintType: ArriveByPickupConstraintType,
    requestId: string,
  ): void {
    if (intentType === RequestIntentType.LeaveAt && !pickupWindow) {
      throw new UnexpectedError('LeaveAt request missing pickup window', { requestId })
    }

    if (intentType === RequestIntentType.ArriveBy && !dropoffWindow) {
      throw new UnexpectedError('ArriveBy request missing dropoff window', { requestId })
    }

    if (
      intentType === RequestIntentType.ArriveBy &&
      arriveByPickupConstraintType === ArriveByPickupConstraintType.Window &&
      !pickupWindow
    ) {
      throw new UnexpectedError('ArriveBy request with two windows missing pickup window', {
        requestId,
      })
    }
  }
}
